import React from 'react';

import { downloadCommsCsv } from './liveDataAdapter';
import { NO_VALUE, type Report, metricFormat } from './presentationModel';
import { productMeasureRows } from './productMeasureRows';

/** Additional self-serve access to measures present at the extract's product grain. */
export default function ProductMeasures({ report }: { report: Report }) {
  const rows = productMeasureRows(report);
  const headers = [
    report.clickDimension === 'Product Name' ? 'Product' : report.clickDimension,
    'Clicks',
    'Items',
    'GM (A$)',
    'TTV (A$)',
    'Items / Clicks',
    'GM / Click (A$)',
  ];
  const exportCsv = () => downloadCommsCsv(report, 'products');

  return (
    <details style={{ margin: '12px 4px 16px', fontSize: 12 }}>
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
        {report.clickDimension === 'Product Name'
          ? 'Explore available product measures'
          : 'Explore available ' + report.clickDimension + ' measures'}
      </summary>
      <p style={{ fontSize: 11, lineHeight: 1.6, margin: '8px 0' }}>
        Click-source items and same-offer GM/TTV at the declared split grain. Custom Message input and worksheet-only
        source restrictions do not filter this separate source. Separate worksheet panes contain content, position,
        offer and vendor breakdowns. Items differ from send-source orders.
      </p>
      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {headers.map((h) => (
                <th key={h} style={{ textAlign: h === 'Product' ? 'left' : 'right', padding: 8, whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name}>
                {[
                  r.name,
                  metricFormat(r.clicks, 'clicks'),
                  metricFormat(r.items, 'orders'),
                  metricFormat(r.gm, 'gm'),
                  metricFormat(r.ttv, 'ttv'),
                  metricFormat(r.cr, 'purchaseRate'),
                  r.gmPerClick == null
                    ? NO_VALUE
                    : new Intl.NumberFormat('en-AU', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(
                        r.gmPerClick,
                      ),
                ].map((v, i) => (
                  <td
                    key={i}
                    style={{
                      textAlign: i ? 'right' : 'left',
                      padding: 8,
                      borderBottom: '1px solid rgba(128,128,128,.25)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {v}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        disabled={!report.ready}
        onClick={exportCsv}
        style={{ fontSize: 11, padding: '6px 9px', marginTop: 8, border: '1px solid currentColor', borderRadius: 4 }}
      >
        Export product measures CSV
      </button>
    </details>
  );
}
